import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { CSSTransition } from 'react-transition-group';
import { primaryHeaderHeights } from '../../../header.utils';
import { MenuLink } from './MenuLink';
import { getActiveEdition } from '../../Editions';
import {
  linkEnterDelay,
  linkEnterDuration, linkEnterTimeOut, linkExitDelay, linkExitDuration, linkExitTimeOut,
  menuEnterDuration,
  menuEnterTimeOut,
  menuExitDelay,
  menuExitDuration,
  menuExitTimeOut,
} from './menuLinks.utils';
import { useTheme } from '../../../../../../../theming/useTheme';
import { ErrorBoundary } from '../../../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../../../mediaQueries.const';
import { BaseUnit } from '../../../../../../../theming/baseUnitDefinition';
import { LinkButtonDataProps } from '../../../../utils';
import { MenuButton } from './MenuButton';
import { NavigationLink } from '../../PrimaryHeader';
import { MenuLinkDataProps } from './MenuSubLink';

const componentConfigThemeKey = 'menuLinks';

export interface MenuLinksDataProps {
  menuLinks: MenuLinkDataProps[];
  buttons?: Array<LinkButtonDataProps>;
  editions: NavigationLink[] | null;
  editionEndpoint: string;
}

export interface MenuLinksThemeProps {
  backgroundColor: string;
  dividerColor: string;
  baseUnit: BaseUnit;
}

interface MenuLinksLogicProps {
  isMenuOpen: boolean;
  onClick: (index: number) => void;
  currentOpenLink: number;
}

const getMenuLinksStyle = (backgroundColor: string, dividerColor: string, baseUnit: BaseUnit) => StyleSheet.create({
  style: {
    padding: 0,
    margin: 0,
    backgroundColor,
    position: 'fixed',
    width: '100%',
    zIndex: 2,
    left: 0,
    overflowY: 'auto',
    [MEDIA_BREAKPOINTS.medium]: {
      top: `${primaryHeaderHeights.medium}px`,
      height: `calc(100vh - ${primaryHeaderHeights.medium}px)`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      top: `${primaryHeaderHeights.small}px`,
      height: `calc(100vh - ${primaryHeaderHeights.small}px)`,
    },
  },
  enter: {
    opacity: 0,
    transform: 'translate(-100%)',
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${menuEnterDuration}ms, opacity ${menuEnterDuration}ms`,
    transitionTimingFunction: 'ease-out',
  },
  exit: {
    opacity: 1,
    transform: 'translate(0)',
  },
  exitActive: {
    opacity: 0,
    transform: 'translate(-100%)',
    transition: `transform ${menuExitDuration}ms ease-in , opacity ${menuExitDuration}ms ease-in`,
    transitionDelay: `${menuExitDelay}ms`,
  },
  divider: {
    backgroundColor: dividerColor,
    height: '1px',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`,
    },
  },
});

const getMenuLinkStyle = () => StyleSheet.create({
  enter: {
    opacity: 0.5,
    transform: 'translate(-100%)',

  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${linkEnterDuration}ms ease-out, opacity ${linkEnterDuration}ms ease-in`,
    transitionDelay: `${linkEnterDelay}ms`,

  },
  exit: {
    transform: 'translate(0)',
    opacity: 1,
  },
  exitActive: {
    opacity: 0.5,
    transform: 'translate(-100%)',
    transition: `transform ${linkExitDuration}ms ease-in, opacity ${linkExitDuration}ms ease-in`,
    transitionDelay: `${linkExitDelay}ms`,
  },
});

interface MenuLinkAnimationWrapperProps {
  shouldAppear: boolean;
}

const MenuLinkAnimationWrapper: React.FunctionComponent<MenuLinkAnimationWrapperProps> = props => {
  const { children, shouldAppear } = props;
  const style = getMenuLinkStyle();
  return (
    <CSSTransition
      unmountOnExit
      mountOnEnter
      in={shouldAppear}
      timeout={{
        enter: linkEnterTimeOut,
        exit: linkExitTimeOut,
      }}
      classNames={{
        enter: css(style.enter),
        enterActive: css(style.enterActive),
        exit: css(style.exit),
        exitActive: css(style.exitActive),
      }}
    >
      {children}
    </CSSTransition>
  );
};

export const createMenuLink = (onClick: (index: number) => void, currentOpenLink: number, shouldAppear: boolean, startIndex = 0) => (menuLink: MenuLinkDataProps, index: number) => {
  return (
    <MenuLinkAnimationWrapper shouldAppear={shouldAppear} key={index}>
      {(state: string) => (
        <MenuLink
          key={index}
          link={menuLink.link}
          childrenLinks={menuLink.childrenLinks}
          index={startIndex + index}
          onClick={onClick}
          isLinkOpen={(currentOpenLink !== -1) && (currentOpenLink === startIndex + index)}
          startChevronIconAnimation={(state === 'entering') || (state === 'entered')}
        />
      )}
    </MenuLinkAnimationWrapper>
  );
};

export const navigationLinkToMenuLinkDataProps: (navigationLink: NavigationLink) => MenuLinkDataProps = (navigationLink: NavigationLink) => ({
  link: navigationLink.link,
  childrenLinks: navigationLink.children ? navigationLink.children.map(navigationLinkToMenuLinkDataProps) : null,
});

export const createEditionLink = (onClick: (index: number) => void, currentOpenLink: number, shouldAppear: boolean, startIndex = 0) => (navigationLink: NavigationLink, index: number) => {
  return (
    <MenuLinkAnimationWrapper shouldAppear={shouldAppear} key={index}>
      {(state: string) => (
        <MenuLink
          key={index}
          link={navigationLink.link}
          childrenLinks={navigationLink.children ? navigationLink.children.map(navigationLinkToMenuLinkDataProps) : null}
          index={startIndex + index}
          onClick={onClick}
          isLinkOpen={(currentOpenLink !== -1) && (currentOpenLink === startIndex + index)}
          startChevronIconAnimation={(state === 'entering') || (state === 'entered')}
        />
      )}
    </MenuLinkAnimationWrapper>
  );
};

const createMenuButton = (shouldAppear: boolean) => ({ text, onClick }: LinkButtonDataProps, key: number) => (
  <MenuLinkAnimationWrapper shouldAppear={shouldAppear} key={key}>
    <MenuButton text={text} onClick={onClick} />
  </MenuLinkAnimationWrapper>
);

export const createEditionsLinks = (editions: NavigationLink[], editionEndpoint: string) => {
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  return [{
    link: {
      href: activeEdition.link.href,
      text: activeEdition.link.text,
    },
    children: editions.map(edition => ({
      link: {
        href: edition.link.href,
        text: edition.link.text,
      },
      children: null,
    })),
  }];
};

export const MenuLinks: React.FunctionComponent<MenuLinksDataProps & MenuLinksLogicProps> = props => {
  const { menuLinks, editions, isMenuOpen, onClick, currentOpenLink, editionEndpoint, buttons = [] } = props;
  const { backgroundColor, dividerColor, baseUnit } = useTheme<MenuLinksThemeProps>(componentConfigThemeKey);
  const style = getMenuLinksStyle(backgroundColor, dividerColor, baseUnit);
  const editionsLinks = editions ? createEditionsLinks(editions, editionEndpoint) : [];
  return (
    <ErrorBoundary>
      <CSSTransition
        in={isMenuOpen}
        unmountOnExit
        mountOnEnter
        timeout={{
          enter: menuEnterTimeOut,
          exit: menuExitTimeOut,
        }}
        classNames={{
          enter: css(style.enter),
          enterActive: css(style.enterActive),
          exit: css(style.exit),
          exitActive: css(style.exitActive),
        }}
      >
        {(state: string) => (
          <ul className={css(style.style)}>
            {menuLinks.map(createMenuLink(onClick, currentOpenLink, (state === 'entering') || (state === 'entered')))}
            {buttons.map(createMenuButton((state === 'entering') || (state === 'entered')))}
            <div className={css(style.divider)} />
            {editionsLinks.map(createEditionLink(onClick, currentOpenLink, (state === 'entering') || (state === 'entered'), menuLinks.length))}
          </ul>
        )}
      </CSSTransition>
    </ErrorBoundary>
  );
};
